import {
	Button,
	Flex,
	Group,
	List,
	Modal,
	Paper,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentUserQueryOptions } from "@/users/operations/get-current-user/getCurrentUserQueryOptions";
import { UserRole } from "@/users/models/UserRole";
import {
	IconClock,
	IconHandGrab,
	IconInfoCircle,
	IconMessageCircle,
	IconX,
} from "@tabler/icons-react";
import { buildSetPreferencesMutation } from "@/common/hooks/buildSetPreferencesMutation";
import { buildPushNotificationsRequestPermissionsMutation } from "../hooks/buildPushNotificationsRequestPermissionsMutation";
import { buildPushNotificationsRegisterMutation } from "../hooks/buildPushNotificationsRegisterMutation";
import { mergeMutationOptions } from "@/common/mutations/mergeMutationOptions";
import { useLogger } from "@/logging/client/LoggerContext";

export const PUSH_PREF_KEY = "push_notifications_in_app_declined";

export function PushNotificationsPermissionsModal({
	opened,
	setOpened,
}: {
	opened: boolean;
	setOpened: (opened: boolean) => void;
}) {
	const logger = useLogger();
	const queryClient = useQueryClient();
	const currentUserQuery = useQuery(getCurrentUserQueryOptions());

	const setPushNotificationsPrefMutation = useMutation(
		buildSetPreferencesMutation(queryClient),
	);
	const registerPushNotificationsMutation = useMutation(
		mergeMutationOptions(buildPushNotificationsRegisterMutation(), {
			onSettled: () => {
				setOpened(false);
				logger.info(
					`Push Notifications Permissions Modal Register Mutation Success`,
				);
			},
			onError: (e) => {
				logger.error(
					`Push Notifications Permissions Modal Register Mutation Error: ${e}`,
				);
			},
		}),
	);
	const requestPushNotificationsPermissionMutation = useMutation(
		mergeMutationOptions(
			buildPushNotificationsRequestPermissionsMutation(queryClient),
			{
				onSuccess: () => {
					registerPushNotificationsMutation.mutate();
				},
				onError: (e) => {
					setOpened(false);
					logger.error(
						`Push Notifications Request Permissions Mutation Error: ${e}`,
					);
				},
			},
		),
	);

	const role = currentUserQuery.data?.role as UserRole;

	const handleEnableNotifications = async () => {
		requestPushNotificationsPermissionMutation.mutate();
		logger.info(`Push Notifications Permissions Modal Enable`);
	};

	const handleDeclineNotifications = () => {
		setPushNotificationsPrefMutation.mutate(
			{
				key: PUSH_PREF_KEY,
				value: "true",
			},
			{
				onSettled: () => {
					setOpened(false);
				},
			},
		);
		logger.info(`Push Notifications Permissions Modal Decline`);
	};

	const getReasonsForRole = () => {
		if (role === UserRole.Caregiver) {
			return [
				{
					icon: <IconClock size={16} />,
					label: "Check in and check out reminders",
				},
				{
					icon: <IconMessageCircle size={16} />,
					label: "Chat messages",
				},
				{
					icon: <IconHandGrab size={16} />,
					label: "Shift pickup requests",
				},
			];
		}

		if (role === UserRole.Client) {
			return [
				{
					icon: <IconClock size={16} />,
					label: "Visit reminders and updates",
				},
				{
					icon: <IconInfoCircle size={16} />,
					label: "Important information",
				},
			];
		}

		return [
			{
				icon: <IconMessageCircle size={16} />,
				label: "Chat messages",
			},
			{
				icon: <IconHandGrab size={16} />,
				label: "Shift Pickup alerts",
			},
			{
				icon: <IconInfoCircle size={16} />,
				label: "Important information",
			},
		];
	};

	return (
		<Modal
			opened={opened}
			onClose={handleDeclineNotifications}
			size="md"
			centered
			keepMounted={false}
		>
			<Stack gap="lg" p="md">
				<Title order={3} ta="center">
					Enable Push Notifications
				</Title>

				<Paper withBorder p="md" radius="md">
					<Text size="sm" mb="md">
						We'd like to send you notifications to keep you updated. Here's how
						notifications help you:
					</Text>

					<List spacing="xs" size="sm" mt="md" center>
						{getReasonsForRole().map((reason) => (
							<List.Item key={reason.label} icon={reason.icon}>
								{reason.label}
							</List.Item>
						))}
					</List>
				</Paper>

				<Text size="sm" c="dimmed" ta="center">
					You can always change notification settings later in your device
					settings.
				</Text>

				<Group justify="space-between" mt="md">
					<Button
						variant="subtle"
						onClick={handleDeclineNotifications}
						leftSection={<IconX size={16} />}
						loading={setPushNotificationsPrefMutation.isPending}
					>
						Not Now
					</Button>
					<Button
						onClick={handleEnableNotifications}
						loading={
							requestPushNotificationsPermissionMutation.isPending ||
							registerPushNotificationsMutation.isPending
						}
					>
						Enable Notifications
					</Button>
				</Group>
			</Stack>
		</Modal>
	);
}
